/**
 * Shared TREATMENT:BUILD launcher.
 *
 * Four surfaces start a practice build: the "New experiment" action on
 * `/experiments`, the "Build experiment" button on an `action` atom inside the
 * user's own guide, the same button on the Together (shared guide) part of the
 * merged reader, and the "Build a new experiment" pill on a Finding's frontier
 * beat. All four need the identical four steps — swap to the chat page, push
 * the URL, force a TREATMENT:BUILD switch, auto-send an opener — and the
 * `force` flag in step three is load-bearing in a way that is easy to drop
 * when copy-pasting, so the sequence lives here once.
 *
 * The only thing that varies is the opener and how the session is seeded: a
 * cold build asks the user what they want to work on; a guide-launched build
 * arrives already knowing which move it is building via `guideAction` identity
 * (the guide action atom; resolved server-side) or `sharedGuideAction` identity
 * (the Together atom; resolved through the shared-guide read path); a
 * Finding-launched build arrives knowing which closed experiment it follows on
 * from via `findingAction` identity (the frontier link; also resolved
 * server-side); and, for legacy sessions, prose `handoffContext` built by the
 * server and merely relayed by the client.
 *
 * @module orchestration/practiceBuildLauncher
 */

// Module-scoped in-flight guard: a double-click/re-entrant call must not spawn
// two overlapping `force:true` sessions — switchMode has no mutex of its own
// (same pattern as `composeNoteLauncher.js`). Kept at the launcher level
// rather than per-caller so it covers every entry point (cold button, guide
// atom, Together atom, finding link) at once.
let launchInFlight = false;

/**
 * Launch a TREATMENT:BUILD session.
 *
 * @param {Object} [options={}]
 * @param {Object|null} [options.handoffContext=null] - Server-authored prose seeding
 *   `<handoff_context>` and flipping the strategy's `isDirected` branch. Must carry
 *   BOTH `focusArea` and `whatTheyWant` or the backend treats the entry as a cold
 *   build (`TreatmentStrategy._buildBuildContext`). Used by legacy sessions only —
 *   the guide action atom and the finding frontier link send identity instead, never this.
 * @param {Object|null} [options.guideAction=null] - `{guideId, sectionType, blockIndex}`
 *   identity for a guide action atom. The backend resolves this server-side
 *   (entitlement-gated, `TreatmentStrategy._resolveGuideActionHandoff`) into the
 *   same handoff fields; the client sends no prose. Any resolution failure
 *   degrades to a cold build with a named admin event, same UX as a missing
 *   `handoffContext` but loud instead of silent.
 * @param {Object|null} [options.sharedGuideAction=null] -
 *   `{sharedGuideDefinitionId, sectionType, blockIndex}` identity for an action
 *   atom on the Together part of the merged reader. Deliberately a distinct key
 *   from `guideAction` and never a `UserGuide` id or the client-visible typed
 *   `shared:` id: the backend resolves it through
 *   `sharedGuideService.resolveForViewer` (bilateral + entitlement gates,
 *   `TreatmentStrategy._resolveSharedGuideActionHandoff`) and degrades to a cold
 *   build with a named admin event on any refusal.
 * @param {Object|null} [options.findingAction=null] - `{practiceId}` identity for a
 *   Finding's frontier link. The backend resolves this server-side
 *   (`TreatmentStrategy._resolveFindingActionHandoff`) into the same handoff
 *   fields, gated on ownership + completed status + an unpursued frontier; any
 *   resolution failure degrades to a cold build the same way as `guideAction`.
 *   The composer sub-agent never sees the resolved handoff, so `opener` must
 *   still carry the frontier text itself — this key alone is not enough.
 * @param {string} [options.opener] - Synthetic first user message
 * @param {string} [options.source] - `metadata.source` stamped on that message
 * @returns {Promise<boolean>} Whether the build session was launched
 */
export async function launchPracticeBuild({
  handoffContext = null,
  guideAction = null,
  sharedGuideAction = null,
  findingAction = null,
  opener = "Let's build an experiment",
  source = 'add_practice_button'
} = {}) {
  if (launchInFlight) return false;

  const orchestrator = window.app?.modules?.modeOrchestrator;
  if (!orchestrator) {
    console.error('[practiceBuildLauncher] No orchestrator available; cannot launch build');
    return false;
  }

  launchInFlight = true;
  try {
    const swapped = await window.app?.modules?.pageManager?.showPage('chat');
    if (swapped === false) return false;
    window.history.pushState({}, '', '/chat');

    // `force: true` is REQUIRED: mode persists across page navigation, so either
    // entry point can fire while the user is still in a TREATMENT session (e.g.
    // just finished a check-in, then opened /practices or a guide). Without
    // force, switchMode's same-mode early-return no-ops the switch and the
    // opener below strands in the prior practice's session.
    await orchestrator.switchMode('TREATMENT', {
      subMode: 'build',
      skipWelcome: true,
      force: true,
      ...(handoffContext ? { handoffContext } : {}),
      ...(guideAction ? { guideAction } : {}),
      ...(sharedGuideAction ? { sharedGuideAction } : {}),
      ...(findingAction ? { findingAction } : {})
    });
    await orchestrator.handleUserMessage(opener, { metadata: { source } });
    return true;
  } catch (error) {
    console.error('[practiceBuildLauncher] Failed to launch build mode', error);
    window.notificationManager?.showError('Could not open the conversation. Please try again.');
    return false;
  } finally {
    launchInFlight = false;
  }
}
